import NetworkTitle from '@/components/CustomComponent/NetworkTitle';
import { COMMON_TEXT } from '@/helpers/common-text';
import {
  CSV_DOWNLOAD_COMPONENT,
  HEATMAP_SNR_COLORS,
  MEMORY_STORE,
  PERFORMANCE_COMPONENT,
  QUERY_STRING_STORE,
} from '@/helpers/constants';
import { compareAsc, format } from 'date-fns';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { SelectButton } from 'primereact/selectbutton';
import React, { Component, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import Draggable from 'react-draggable';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import CustomCSVDownload from 'src/components/CustomComponent/CustomCSVDownload/CustomCSVDownload';
import HeatMap from 'src/components/CustomComponent/HeatMap';
import LoadingSpinner from 'src/components/CustomComponent/LoadingSpinner';
import {
  checkListChanges,
  transformPositionToMarkerCoordinate,
} from 'src/helpers/utility';
import { CustomMarker } from 'src/helpers/wireless-signal-marker';
import { fetchDataFloorMap, fetchDataHeatMap } from './query-request';
import { ZoomInIcon, ZoomOutIcon, ZoomResetIcon } from './icons';

const DEFAULT_BUFFER = 12;
const INITIAL_SCALE = 1.0;

// Custom ImageMarker component with external zoom control access (copied from PeopleCounts)
const CustomImageMarker = ({
  src,
  markers,
  onAddMarker,
  markerComponent: MarkerComponent,
  bufferLeft = DEFAULT_BUFFER,
  bufferTop = DEFAULT_BUFFER,
  alt = 'Markers',
  extraClass = '',
  isDisableDragMarker = true,
  onMarkerClicked,
  onTransformRefSet,
}) => {
  const imageRef = useRef(null);
  const [isDisablePan, setIsDisablePan] = useState(false);
  const [currentScaleValue, setCurrentScaleValue] = useState(INITIAL_SCALE);

  const handleImageClick = useCallback(
    event => {
      if (!imageRef.current || !onAddMarker) {
        return;
      }
      const imageDimensions = imageRef.current.getBoundingClientRect();
      const rect = imageDimensions;
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      const top = ((y - bufferTop) / rect.height) * 100;
      const left = ((x - bufferLeft) / rect.width) * 100;

      onAddMarker({
        top: Math.max(0, Math.min(100, top)),
        left: Math.max(0, Math.min(100, left)),
      });
    },
    [onAddMarker, bufferLeft, bufferTop]
  );

  const markerOnClicked = useCallback(
    ({ data }) => {
      if (onMarkerClicked) {
        onMarkerClicked({ data });
      }
    },
    [onMarkerClicked]
  );

  const getItemPosition = marker => {
    const { top, left } = marker;
    return {
      top: `${top}%`,
      left: `${left}%`,
    };
  };

  const startDrag = useCallback(() => {
    setIsDisablePan(true);
  }, []);

  const stopDrag = useCallback(() => {
    setIsDisablePan(false);
  }, []);

  return (
    <TransformWrapper
      wheel={{ step: 0.1, disabled: true }}
      minScale={0.2}
      maxScale={2.0}
      initialScale={INITIAL_SCALE}
      panning={{ disabled: isDisablePan }}
      onTransformed={(ref, event) => {
        setCurrentScaleValue(ref.state.scale);
      }}
    >
      {({ zoomIn, zoomOut, resetTransform, ...rest }) => {
        // Pass transform functions to parent component
        if (onTransformRefSet) {
          onTransformRefSet({ zoomIn, zoomOut, resetTransform });
        }
        
        return (
          <TransformComponent
            wrapperStyle={{
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '100%',
            }}
            contentStyle={{
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '100%',
            }}
          >
            <div className="image-marker">
              <img
                src={src}
                alt={alt}
                onClick={handleImageClick}
                className={`image-marker__image ${extraClass}`}
                ref={imageRef}
              />
              {markers && markers.map((marker, itemNumber) => (
                <Draggable
                  key={`draggable-marker-${itemNumber}`}
                  onStart={startDrag}
                  onStop={stopDrag}
                  disabled={isDisableDragMarker}
                  scale={currentScaleValue}
                >
                  <div
                    className={`image-marker__marker ${
                      !MarkerComponent && 'image-marker__marker--default'
                    }`}
                    key={itemNumber}
                    style={{ ...getItemPosition(marker), cursor: 'pointer' }}
                    data-testid="marker"
                  >
                    {MarkerComponent ? (
                      <MarkerComponent
                        {...marker}
                        itemNumber={itemNumber}
                        markerOnClicked={markerOnClicked}
                      />
                    ) : (
                      itemNumber + 1
                    )}
                  </div>
                </Draggable>
              ))}
            </div>
          </TransformComponent>
        );
      }}
    </TransformWrapper>
  );
};

@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class WirelessSignal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      floor:
        this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0],
      heatMapData: [],
      xAxisHeatMapData: [],
      yAxisHeatMapData: [],
      floorMapData: [],
      isLoadingHeatMap: false,
      isLoadingFloorMap: false,
      selectedSignalType: 0,
      floorMapError: false,
      floorMapUrl: '',
      csvDownload: false,
    };
    this.signalOptions = [
      { name: '2.4 Ghz', value: 0 },
      { name: '5.0 Ghz', value: 1 },
    ];
    this.transformRef = null; // Add transform ref for zoom controls
  }

  // Zoom control methods (copied from PeopleCounts)
  setTransformRef = (ref) => {
    this.transformRef = ref;
  };

  handleZoomIn = () => {
    if (this.transformRef) {
      this.transformRef.zoomIn();
    }
  };

  handleZoomOut = () => {
    if (this.transformRef) {
      this.transformRef.zoomOut();
    }
  };

  handleZoomReset = () => {
    if (this.transformRef) {
      this.transformRef.resetTransform();
    }
  };

  validateFloorMapUrl(url, successCallback, errorCallback) {
    var img = new Image();
    img.onload = function () {
      successCallback(url);
    };
    img.onerror = function () {
      errorCallback(url);
    };
    img.src = url;
  }

  getFloorMapUrl = floorId => {
    const floors = this.props?.sessionStore[MEMORY_STORE.FLOORS] || [];
    const floor = floors.find(floor => floor.floor_id === floorId);
    this.validateFloorMapUrl(
      floor?.floor_map_url,
      () => {
        this.setState({ floorMapUrl: floor?.floor_map_url, floorMapError: false });
      },
      () => {
        this.setState({ floorMapUrl: '', floorMapError: true });
      }
    );
    return floor ? floor.floor_map_url : '';
  };

  componentDidMount() {
    const { floor } = this.state;
    if (floor) {
      this.getFloorMapUrl(floor);
      this.getDataFloorMap();
    }

    const { selectedDate } = this.props;
    if (floor && selectedDate) {
      this.getDataHeatMap();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      querystring,
      deviceInfoOrderedList: deviceList,
      selectedDate,
    } = this.props;
    const {
      querystring: prevQuerystring,
      selectedDate: prevSelectedDate,
      deviceInfoOrderedList: prevDeviceList,
    } = prevProps;
    const floor = querystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0];
    const prevFloor =
      prevQuerystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0];
    const isFloorChanged = floor !== prevFloor;
    if (isFloorChanged) {
      this.setState({ floor }, () => {
        this.getFloorMapUrl(floor);
      });
    }
    const isDateChanged = selectedDate !== prevSelectedDate;
    const { elementsChanged, orderChanged } = checkListChanges(
      deviceList,
      prevDeviceList,
      'deviceId'
    );
    if (elementsChanged || isDateChanged) {
      this.setState({ floor }, () => {
        this.getDataHeatMap();
        this.getDataFloorMap();
      });
    } else if (orderChanged) {
      this.triggerUpdateDataSorting();
    }
  }

  triggerUpdateDataSorting = () => {
    const { heatMapData } = this.state;
    const { deviceInfoOrderedList } = this.props;
    const deviceIdList = deviceInfoOrderedList.map(item => item.deviceId);
    const {
      heatMapData: sortedHeatMapData,
      xAxis,
      yAxis,
    } = this.sortDataHeatMap({
      heatMapData,
      orderedDeviceIdList: deviceIdList,
    });

    const newState = {
      heatMapData: sortedHeatMapData,
      xAxisHeatMapData: xAxis,
      yAxisHeatMapData: yAxis,
    };
    this.setState(newState);
  };

  computeXAxisHeatMap = heatMapData => {
    const xAxis = [];
    const xAxisLabelSet = new Set(heatMapData.map(item => item.x));
    const xAxisLabelArray = Array.from(xAxisLabelSet).map(label => ({
      label,
      datetime: new Date(label.split(' ')[0] + 'T' + label.split(' ')[1]),
    }));
    if (xAxisLabelArray.length === 0) {
      return xAxis;
    }

    // Sort the array by datetime
    xAxisLabelArray.sort((a, b) => a.datetime - b.datetime);

    for (let i = 0; i < xAxisLabelArray.length; i++) {
      const labels = xAxisLabelArray[i].label.split(' ');
      const labelTime = labels[1].substring(0, 5);
      const categoryDate = labels[0];
      const datetime = xAxisLabelArray[i].datetime;

      let categoryLabel = '';
      if (datetime.getHours() % 3 === 0 && datetime.getMinutes() === 0) {
        categoryLabel = labelTime;
      }

      xAxis.push({
        category: xAxisLabelArray[i].label,
        id: `xAxisLabel-${i}`,
        categoryLabel: categoryLabel,
        categoryDate: categoryDate,
      });
    }
    return xAxis;
  };

  computeYAxisHeatMap = heatMapData => {
    const yAxis = [];
    const yAxisLabelSet = new Set();
    const yAxisLabelArray = [];
    for (const data of heatMapData) {
      if (!yAxisLabelSet.has(data['device_identifer'])) {
        yAxisLabelSet.add(data['device_identifer']);
        yAxisLabelArray.push({
          deviceName: data.y,
          deviceId: data['device_identifer'],
        });
      }
    }
    for (let i = 0; i < yAxisLabelArray.length; i++) {
      yAxis.push({
        deviceId: yAxisLabelArray[i]['deviceId'],
        category: yAxisLabelArray[i]['deviceName'],
        id: `yAxisLabel-${i}`,
      });
    }
    return yAxis.sort((a, b) => b.category.localeCompare(a.category));
  };

  // This function is used to fill the missing data in the heat map
  fillHeatMapData(xAxis, yAxis, heatMapData) {
    // Convert heatMapData into a map for fast lookup
    let dataMap = new Map(
      heatMapData.map(item => [`${item.x}|${item.y}`, item])
    );

    xAxis.forEach(xItem => {
      yAxis.forEach(yItem => {
        let key = `${xItem.category}|${yItem.category}`;

        if (!dataMap.has(key)) {
          heatMapData.push({
            device_identifer: yItem.deviceId,
            x: xItem.category,
            y: yItem.category,
            columnSettings: {
              fill: '#ffffff', // Use default fill color or specify as needed
            },
            value: -1,
          });
        }
      });
    });

    // Return the updated heat map data
    return heatMapData;
  }

  getDataFloorMap = async () => {
    const { floor } = this.state;
    this.setState({ isLoadingFloorMap: true });
    let dataFloorMap = await fetchDataFloorMap({
      floorId: floor,
    });

    if (!dataFloorMap) {
      dataFloorMap = [];
    }

    let updatedFloorMapData = dataFloorMap.map(item => ({
      position: {
        x: parseFloat(item?.position_x),
        y: parseFloat(item?.position_y),
      },
      color: HEATMAP_SNR_COLORS[item?.grade?.toUpperCase()] ?? '#ffffff',
      data: item ?? null,
    }));

    this.setState({
      floorMapData: updatedFloorMapData,
      isLoadingFloorMap: false,
    });
  };

  sortDataHeatMap({ heatMapData, orderedDeviceIdList }) {
    const xAxis = this.computeXAxisHeatMap(heatMapData);
    let yAxis = this.computeYAxisHeatMap(heatMapData);

    heatMapData = this.fillHeatMapData(xAxis, yAxis, heatMapData);
    const orderMapping = orderedDeviceIdList.reduce((acc, deviceId, index) => {
      acc[deviceId] = index;
      return acc;
    }, {});
    yAxis = yAxis.sort((a, b) => {
      const indexA = orderMapping[b['deviceId']] ?? -1;
      const indexB = orderMapping[a['deviceId']] ?? -1;
      // If both indices are defined, sort normally.
      if (indexA !== undefined && indexB !== undefined) {
        return indexA - indexB || b.category.localeCompare(a.category);
      }

      // If one index is undefined, move it to the end.
      if (indexA === undefined && indexB !== undefined) {
        return 1;
      }
      if (indexA !== undefined && indexB === undefined) {
        return -1;
      }

      // If both indices are undefined, leave them as they are.
      return 0;
    });

    return { heatMapData, xAxis, yAxis };
  }

  getDataHeatMap = async () => {
    let { floor, heatMapData: currentHeatMapData } = this.state;
    const {
      deviceInfoOrderedList,
      selectedDate,
      addedDeviceIdList,
      isResetDeviceIdList,
    } = this.props;
    const deviceIdList = deviceInfoOrderedList.map(item => item.deviceId);
    if (!addedDeviceIdList || addedDeviceIdList.length === 0 || !floor) {
      return;
    }
    if (isResetDeviceIdList) {
      currentHeatMapData = [];
    }
    this.setState({ isLoadingHeatMap: true });
    let dataHeatMap = await fetchDataHeatMap({
      floorId: floor,
      fromDate: format(selectedDate, 'yyyy-MM-dd 00:00:00'),
      toDate: format(selectedDate, 'yyyy-MM-dd 23:59:59'),
      deviceIdList: addedDeviceIdList,
    });

    if (!dataHeatMap) {
      dataHeatMap = [];
    }

    let updatedHeatMapData = dataHeatMap
      .map(item => ({
        device_identifer: item.device_identifer,
        x: item.timestamp,
        y: item.device_name,
        columnSettings: {
          fill: HEATMAP_SNR_COLORS[item?.grade?.toUpperCase()] ?? '#ffffff',
        },
        value: item.snr,
      }))
      .sort((a, b) => compareAsc(new Date(a.x), new Date(b.x)));

    currentHeatMapData = currentHeatMapData.concat(updatedHeatMapData);

    let { heatMapData, xAxis, yAxis } = this.sortDataHeatMap({
      heatMapData: currentHeatMapData,
      orderedDeviceIdList: deviceIdList,
    });

    this.setState(
      {
        heatMapData,
        xAxisHeatMapData: xAxis,
        yAxisHeatMapData: yAxis,
        isLoadingHeatMap: false,
      },
      () => {}
    );
  };

  renderMessageError = () => {
    const { floor } = this.state;
    if (!floor) {
      return (
        <Message
          severity="error"
          text={COMMON_TEXT.FLOOR_NOT_SELECTED_PLEASE_SELECT}
        />
      );
    }
  };

  getTimeFromDate = timestamp => {
    const date = new Date(timestamp * 1000);
    const hours = date.getHours();
    return hours;
  };

  renderHeatMap(heatMapData, xAxis, yAxis) {
    const { isLoadingHeatMap } = this.state;
    let filteredYAxis = [];
    let filteredData = [];
    if (heatMapData && heatMapData.length > 0) {
      const { selectedDeviceIdLineList } = this.props;
      filteredYAxis = yAxis.filter(item =>
        selectedDeviceIdLineList.some(
          deviceId =>
            deviceId?.toLowerCase() === item['deviceId']?.toLowerCase()
        )
      );
      filteredData = heatMapData.filter(item =>
        selectedDeviceIdLineList.some(
          deviceId =>
            deviceId?.toLowerCase() === item['device_identifer']?.toLowerCase()
        )
      );
    }
    return (
      <HeatMap
        horizontalData={xAxis}
        verticalData={filteredYAxis}
        data={filteredData}
        name="heatmapwirelesssignal"
        tooltipLabelList={['デバイス名', '日時', 'SNR']}
        isLoading={isLoadingHeatMap}
        hideHeader={true}
      />
    );
  }

  selectedSignalTypeOnChanged = e => {
    this.setState({ selectedSignalType: e.value });
  };

  renderSelectSignalType() {
    const { selectedSignalType } = this.state;
    return (
      <SelectButton
        value={selectedSignalType}
        optionLabel="name"
        onChange={this.selectedSignalTypeOnChanged}
        options={this.signalOptions}
        className="select-single-type-style"
        allowEmpty={false}
      />
    );
  }

  renderDownloadButton() {
    const { csvDownload } = this.state;
    return (
      <>
        <Button
          type="button"
          severity={csvDownload ? "secondary" : "info"}
          className={csvDownload ? 'back-button' : 'csv-download-button'}
          onClick={() => {
            this.setState({
              csvDownload: !csvDownload,
            });
          }}
        >
          {csvDownload ? (
            <>
              <i className="pi pi-arrow-left" />
              <span>戻る</span>
            </>
          ) : (
            <>
              <img 
                src="/assets/img/download-icon.svg" 
                alt="Download" 
                width="24" 
                height="24"
              />
              <span>{COMMON_TEXT.CSV_EXPORT}</span>
            </>
          )}
        </Button>
      </>
    );
  }

  render() {
    const {
      heatMapData,
      xAxisHeatMapData,
      yAxisHeatMapData,
      floorMapData,
      isLoadingFloorMap,
      floorMapError,
      floorMapUrl,
      selectedSignalType,
      csvDownload,
      floor,
    } = this.state;
    const { selectedDeviceIdLineList, selectedDate } = this.props;
    let floorMapDataMarkers = floorMapData.map(item => {
      const marker = transformPositionToMarkerCoordinate(item.position);
      return {
        ...marker,
        item: item,
        type: selectedSignalType === 0 ? 'channel_24' : 'channel_5',
      };
    });

    return (
      <div className="network-container wireless-signal">
        {csvDownload ? (
          <div className="wireless-signal-csv-download-container">
            <div className="wireless-signal-csv-download-header">
              <NetworkTitle
                type={PERFORMANCE_COMPONENT.WIRELESS_SIGNAL}
                customTitle='信号品質'
                customColor='#fff'
              ></NetworkTitle>
              {this.renderDownloadButton()}
            </div>
            <CustomCSVDownload
              fromDate={selectedDate}
              toDate={selectedDate}
              type={CSV_DOWNLOAD_COMPONENT.WIRELESS_SIGNAL}
              deviceIdList={[...selectedDeviceIdLineList]}
              floorId={floor}
            ></CustomCSVDownload>
          </div>
        ) : (
          <div className="wireless-signal-content">
            {this.renderMessageError() || (
              <>
                <div className="signal-container-main">
                  <div className="floormap-section">
                    <div className="floormap-controls">
                      <NetworkTitle
                        type={PERFORMANCE_COMPONENT.WIRELESS_SIGNAL}
                        customTitle='信号品質 マップ'
                      ></NetworkTitle>
                      {this.renderSelectSignalType()}
                    </div>
                    <div className="floormap-wrapper">
                      {isLoadingFloorMap ? (
                        <LoadingSpinner />
                      ) : (
                        <div className="data-container-floormap">
                          <CustomImageMarker
                            extraClass="custom-image-marker"
                            bufferLeft={0}
                            bufferTop={0}
                            src={floorMapUrl}
                            markerComponent={CustomMarker}
                            markers={
                              floorMapError ? [] : floorMapDataMarkers || []
                            }
                            alt=""
                            onTransformRefSet={this.setTransformRef}
                          />
                          
                          <div className="zoom-controls">
                            <button
                              className="zoom-out"
                              onClick={this.handleZoomOut}
                              title="ズームアウト"
                            >
                              <ZoomOutIcon />
                            </button>
                            <button
                              className="zoom-reset"
                              onClick={this.handleZoomReset}
                              title="リセット"
                            >
                              <ZoomResetIcon />
                            </button>
                            <button
                              className="zoom-in"
                              onClick={this.handleZoomIn}
                              title="ズームイン"
                            >
                              <ZoomInIcon />
                            </button>
                          </div>
                        </div>
                      )}
                      {floorMapError && (
                        <Message
                          severity="error"
                          className="mt-3 mb-3"
                          text={COMMON_TEXT.CANNOT_LOAD_FLOORMAP}
                        />
                      )}
                    </div>
                  </div>
                  <div className="signal-list-section">
                    <div className="signal-list-header">
                      <NetworkTitle
                        type={PERFORMANCE_COMPONENT.WIRELESS_SIGNAL}
                        customTitle='信号品質 リスト'
                        customColor='#fff'
                      ></NetworkTitle>
                      <div className="csv-download-container">
                        {this.renderDownloadButton()}
                      </div>
                    </div>
                    <div className="signal-list-subtitle">
                      最新のチャンネルとSNR
                    </div>
                    <div className="heatmap-wrapper">
                      {this.renderHeatMap(
                        heatMapData,
                        xAxisHeatMapData,
                        yAxisHeatMapData
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
}

WirelessSignal.propTypes = {};
export default WirelessSignal;
